import { Injectable, Injector } from "@angular/core";
import { HttpWrapperService } from "./http.wrapper.service";
import { SessionManagerService } from "./SessionManager/session-manager.service";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { observableToPromise } from "../admin/events-screen/utils/observableToPromise";
import { Observable } from "rxjs/Observable";
import { ServerNamespace } from "../_models/server/server-namespace";
import { DateAndTimeUtils } from "../_helpers/dateUtils";
import { GenericSearchState } from "../_models/GenericSearchState";
import { DevToolsService } from "./dev-tools.service";
import { FilesService } from "./files.service";
import {
  DepositData,
  PaperPaymentItem,
  PaperPaymentItemBatch,
} from "../admin/receipt/receipt-page-models/PaperPaymentItem";
import { PaperPaymentType } from "../admin/receipt/receipt-page-models/PaperPaymentType";
import { Epoc } from "../_models/Epoc";
import { DiscountManagementService } from "./discount-management.service";
import { ChildAbstractModel } from "../_models/child-abstract-model";
import { sleep } from "../_helpers/sleep";
import { SaveActionResult } from "../admin/events-screen/event-page-models/save-action-result";
import { numberFromString } from "../admin/events-screen/parent-events-mocks/mock-utils-functions";

@Injectable()
export class ReceiptManagementService {
  private readonly openReceiptsInTable$: BehaviorSubject<
    PaperPaymentItemBatch[]
  > = new BehaviorSubject([]);
  private readonly openReceiptsTableSearchState$: BehaviorSubject<
    GenericSearchState & { lastSearchProperties: any }
  > = new BehaviorSubject<GenericSearchState & { lastSearchProperties: any }>(
    null
  );
  private readonly openReceiptsSearchResultsAbstract$: BehaviorSubject<{
    numberOfResults: number;
    numberOfReceiptsIIssued: number;
    numberOfReceiptsOverdue: number;
    totalSum: string;
  }> = new BehaviorSubject<{
    numberOfResults: number;
    numberOfReceiptsIIssued: number;
    numberOfReceiptsOverdue: number;
    totalSum: string;
  } | null>(null);
  private MAX_NUMBER_OF_PARENT_ROWS_ON_SCROLL: 200;

  constructor(
    private http: HttpWrapperService,
    private sessionManager: SessionManagerService,
    private injector: Injector,
    private router: Router,
    private devToolsService: DevToolsService,
    private filesService: FilesService
  ) {}

  public static serverToPaperPaymentItemBatch(
    serverData: ServerNamespace.OpenReceiptSearchResponse
  ): PaperPaymentItemBatch {
    const paymentTypeForBatch =
      serverData.receipt_type === "cheque"
        ? PaperPaymentType.check
        : PaperPaymentType.cash;

    const items: PaperPaymentItem[] = serverData.items.map((item) => {
      const optionalCheckInfo =
        paymentTypeForBatch === PaperPaymentType.check
          ? {
              checkInfo: {
                amount: item.cheque_amount,
                checkNumber: item.cheque_number || "",
                dueDate: item.cheque_date || (0 as any as Epoc),
              },
            }
          : {};

      const leftForDeposit = Number(item.amount_left_to_deposit);

      const amountToPay =
        paymentTypeForBatch === PaperPaymentType.check
          ? leftForDeposit
          : Number(item.final_price);

      return {
        id: item.id,
        childName: item.hanich_name,
        eventName: item.event_name,
        paymentType: paymentTypeForBatch,
        beyondDepositTimeframe: item.is_overdue,
        amountToPay,
        leftForDeposit,
        refundRequested: item.payment_return_request_exists,
        receiptDate: DateAndTimeUtils.dateToEpoch(new Date(item.payment_date)),
        personWhoDeposited: item.issuer_name,
        receiptUrl: item.receipt_url,
        receiptNumber: item.receipt_number,
        isPartiallyDeposit: item.is_partially_deposited,
        ...optionalCheckInfo,
      };
    });
    const isSingleItemInBatch = items.length === 1;
    const someRequestedRefund = items.some((item) => item.refundRequested);
    const customHashId = serverData.batch_id_for_infinite_scroll;
    return {
      id: customHashId,
      childName: isSingleItemInBatch ? items[0].childName : "",
      eventName: serverData.event_name,
      personWhoDeposited: serverData.issuer_name,
      amountToPay: Number(serverData.amount_collected),
      leftForDeposit: Number(serverData.amount_left_to_deposit),
      paymentType: paymentTypeForBatch,
      beyondDepositTimeframe: false,
      refundRequested: someRequestedRefund,
      receiptDate: items.length > 0 ? items[0].receiptDate : (0 as any as Epoc),
      paymentsItems: items,
      isPartiallyDeposit: serverData.is_partially_deposited,
      receiptUrl: "",
      receiptNumber: "",
    };
  }

  public getOpenReceiptsInTable(): Observable<PaperPaymentItemBatch[]> {
    return this.openReceiptsInTable$.asObservable();
  }

  public getOpenReceiptsTableSearchState(): Observable<GenericSearchState> {
    return this.openReceiptsTableSearchState$.asObservable();
  }

  public async depositCashOrChequeReceipt({
    transActionReferenceNumber,
    depositDate,
    depositAmount,
    comments,
    specificPaymentItemId,
    paymentBatch,
    isCheque,
  }: {
    transActionReferenceNumber: number;
    isCheque: boolean;
    depositDate: Epoc;
    depositAmount: number;
    comments: string;
  } & DepositData) {
    try {
      const payment_item_ids = paymentBatch.paymentsItems.map((pi) => pi.id);
      const body = {
        is_cheque_deposit: isCheque,
        deposit_number: transActionReferenceNumber,
        deposit_date: depositDate,
        amount: depositAmount,
        comment: comments,
        payment_item_ids,
      };

      const response = await observableToPromise(
        this.http.post(`/django-api/deposits/`, body)
      );

      if (response.status === 201) {
        this.refreshSearchResults().then();
        return {
          success: true,
          data: response.json(),
        };
      }
      return {
        success: false,
        error: response.json(),
      };
    } catch (e) {
      return {
        success: false,
        error: e,
      };
    }
  }
  public async searchEventByText(
    searchText: string,
    maxResults: number = 10
  ): Promise<ServerNamespace.GetParentEventForSubsidySearch[]> {
    if (!searchText || searchText.length < 3) {
      return [];
    }
    const response = await observableToPromise(
      this.http.get(
        `/django-api/open-receipts/select_event_for_open_receipt_search/`,
        {
          params: { search: searchText },
        }
      )
    );
    const body = await response.json();
    const parentEvents =
      body.results as ServerNamespace.GetParentEventForSubsidySearch[];

    return parentEvents.slice(0, maxResults);
  }

  public async searchReceipts({
    hanich_id,
    parent_event_id,
    kenim,
    mehozot,
    kehilot,
    issuer_id,
    typeOfDeposits = "all",
  }: {
    from_date?: string | null;
    to_date?: string | null;
    hanich_id?: string | null;
    parent_event_id?: string | null;
    kenim?: number[] | null;
    mehozot?: number[] | null;
    kehilot?: number[] | null;
    issuer_id?: number;
    typeOfDeposits: "all" | "partial" | "cheque";
  }) {
    this.openReceiptsTableSearchState$.next({
      lastSearchProperties: {
        hanich_id,
        parent_event_id,
        kenim,
        mehozot,
        kehilot,
        issuer_id,
        typeOfDeposits,
      },
      next: null,
      count: 0,
      previous: null,
      isLoading: true,
    });
    const userSearchParams: any = {
      hanich_id,
      issuer: issuer_id,
      kenim,
      mehozot,
      kehilot,
    };
    if (parent_event_id) {
      userSearchParams["event"] = [parent_event_id];
    }

    if (typeOfDeposits !== "all") {
      if (typeOfDeposits === "partial") {
        userSearchParams["partial_deposits_only"] = "true";
        // userSearchParams["receipt_type"] = "cash";
      } else if (typeOfDeposits === "cheque") {
        userSearchParams["cheques_only"] = true;
        // userSearchParams["receipt_type"] = "cheque";
      }
    }
    let activeParams = {};
    for (const key in userSearchParams) {
      if (userSearchParams[key]) {
        activeParams[key] = userSearchParams[key];
      }
    }
    activeParams = HttpWrapperService.ParamsObjectToCommaArrays(
      activeParams
    ) as any;

    const response = await observableToPromise(
      this.http.get(`/django-api/open-receipts/`, { params: activeParams })
    );
    const responseData =
      (await response.json()) as ServerNamespace.ServerResponse<ServerNamespace.OpenReceiptSearchResponse> & {
        total_issued_by_me: number;
        total_amount_to_deposit: string;
        total_overdue_receipts: number;
        total_search_results: number;
      };
    const openReceiptsFromSearch = responseData.results.map((r) =>
      ReceiptManagementService.serverToPaperPaymentItemBatch(r)
    );

    this.openReceiptsInTable$.next(openReceiptsFromSearch);
    this.openReceiptsTableSearchState$.next({
      lastSearchProperties: {
        hanich_id,
        parent_event_id,
        kenim,
        mehozot,
        kehilot,
        issuer_id,
        typeOfDeposits,
      },
      next: responseData.next,
      count: responseData.count,
      previous: responseData.previous,
      isLoading: false,
    });
    this.openReceiptsSearchResultsAbstract$.next({
      numberOfResults: responseData.total_search_results,
      numberOfReceiptsIIssued: responseData.total_issued_by_me,
      numberOfReceiptsOverdue: responseData.total_overdue_receipts,
      totalSum: responseData.total_amount_to_deposit,
    });
    return openReceiptsFromSearch;
  }
  public getOpenReceiptsSearchResultsAbstract() {
    return this.openReceiptsSearchResultsAbstract$ as Observable<{
      numberOfResults: number;
      numberOfReceiptsIIssued: number;
      numberOfReceiptsOverdue: number;
      totalSum: string;
    } | null>;
  }
  public async searchChildByText(
    searchText: string,
    maxResults: number = 10
  ): Promise<ChildAbstractModel[]> {
    if (!searchText || searchText.length < 3) {
      return [];
    }
    const response = await observableToPromise(
      this.http.get(
        `/django-api/open-receipts/select_hanich_for_open_receipt_search/`,
        {
          params: { search: searchText },
        }
      )
    );
    const body = await response.json();
    const children =
      body.results as ServerNamespace.GetChanichForSubsidySearch[];
    let data = children.map(
      DiscountManagementService.serializeServerChildRequestRequestsToFamilyMember
    );
    data = data.map((child) => ({
      ...child,
      israeliId: this.devToolsService.removeStartsFromIsraeliId(
        child.israeliId
      ),
    }));

    return data.slice(0, maxResults);
  }
  public async searchReceiptIssuerByText(
    searchText: string,
    maxResults: number = 10
  ): Promise<
    Array<{
      id: number;
      firstName: string;
      lastName: string;
      israeliId: string;
    }>
  > {
    if (!searchText || searchText.length < 3) {
      return [];
    }
    const response = await observableToPromise(
      this.http.get(
        `/django-api/open-receipts/select_issuer_for_open_receipt_search/`,
        {
          params: { search: searchText },
        }
      )
    );
    const body = await response.json();
    const users = body.results as any;
    const data = users.map((u) => ({
      id: u.id,
      firstName: u.first_name,
      lastName: u.last_name,
      israeliId: u.taz,
    }));

    return data.slice(0, maxResults);
  }

  public async cancelReceipt(paymentItemId: number): Promise<SaveActionResult> {
    try {
      const response = await observableToPromise(
        this.http.post(`/django-api/payment-items/${paymentItemId}/cancel/`, {})
      );
      if (response.status === 200 || response.status === 204) {
        this.refreshSearchResults().then();
        return {
          success: true,
          data: response.json(),
        };
      }
      return {
        success: false,
        error: response.json(),
      };
    } catch (e) {
      return {
        success: false,
        error: e,
      };
    }
  }
  public async refreshSearchResults() {
    const searchState = this.openReceiptsTableSearchState$.getValue();
    if (!searchState || !searchState.lastSearchProperties) {
      return;
    }
    this.searchReceipts(searchState.lastSearchProperties).then();
  }

  public async scrolledToBottomOfResults() {
    const searchState = this.openReceiptsTableSearchState$.getValue();
    if (!searchState) {
      return;
    }
    const existingReceiptsInTable = this.openReceiptsInTable$.getValue();
    if (
      searchState.isLoading ||
      !searchState.next ||
      existingReceiptsInTable.length > this.MAX_NUMBER_OF_PARENT_ROWS_ON_SCROLL
    ) {
      return;
    }
    try {
      this.openReceiptsTableSearchState$.next({
        ...searchState,
        isLoading: true,
      });
      // scrolling
      const addressParams = searchState.next.split(`open-payments/?`)[1];
      const response = await observableToPromise(
        this.http.get(`django-api/open-receipts/?${addressParams}`)
      );

      const resultsData =
        (await response.json()) as ServerNamespace.ServerResponse<ServerNamespace.OpenReceiptSearchResponse>;
      const serializedReceipts = (resultsData.results || []).map((user) =>
        ReceiptManagementService.serverToPaperPaymentItemBatch(user)
      );

      const updatedReceiptsInTable = [
        ...existingReceiptsInTable,
        ...serializedReceipts,
      ];

      this.openReceiptsInTable$.next(updatedReceiptsInTable);
      this.openReceiptsTableSearchState$.next({
        ...searchState,
        next: resultsData.next,
        count: resultsData.count,
        previous: resultsData.previous,
        isLoading: false,
      });
    } catch (err) {}
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/receipt-management.service.ts